/**
 * #422 — client-side stealth visibility. Pure, `@pit/protocol`-importing
 * helpers that live OUTSIDE `src/render/**` (which may only import
 * `@pit/protocol`/`@pit/sim` as TYPES — see `eslint.config.js`'s
 * render-scope import guard). `src/render/**` consumers (entityView.ts,
 * mount.ts) get a plain precomputed boolean through a resolver function,
 * mirroring how `nameplate.ts` consumes `targetSelectWire.ts` rather than
 * importing `@pit/sim` itself.
 *
 * Stealth is a hashed, ALWAYS-fully-known sim flag (#91) — full aura state
 * is already on the wire for every entity (#30's `WireAura`), by design, for
 * determinism. Nothing server-side is hidden; this module is purely a
 * client RENDER-time filter over data the client already has.
 */

import { STEALTH_AURA_DEF_ID } from "@pit/protocol";
import type { WireAura } from "@pit/protocol";

/**
 * Is `auras` currently stealthed at `tick`? Mirrors `@pit/sim`'s
 * `isStealthed` (targeting.ts) exactly — same def-id + expiry check — just
 * over the wire's `WireAura` shape instead of a full sim `AuraInstance`.
 */
export function isStealthedWire(auras: readonly WireAura[], tick: number): boolean {
  for (const a of auras) {
    if (a.def !== STEALTH_AURA_DEF_ID) continue;
    if (a.expiresAtTick === null || tick < a.expiresAtTick) return true;
  }
  return false;
}

/**
 * Should an entity on `entityTeam` holding `entityAuras` be hidden from a
 * viewer on `viewerTeam` at `tick`? Mirrors `@pit/sim`'s `isTargetableBy`
 * same-team exception:
 *   - `viewerTeam === entityTeam` (self + allies) → never hidden.
 *   - `viewerTeam === null` (unresolved viewer — fail open) → never hidden.
 *   - otherwise (an ENEMY viewer) → hidden iff currently stealthed.
 */
export function isHiddenFromViewer(
  entityTeam: number,
  entityAuras: readonly WireAura[],
  viewerTeam: number | null,
  tick: number,
): boolean {
  if (viewerTeam === null || viewerTeam === entityTeam) return false;
  return isStealthedWire(entityAuras, tick);
}
